import { Outlet, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Sidebar, useSidebar } from './Sidebar';
import { RightSidebar, useRightRailOffset } from './RightSidebar';
import { TopBar } from './TopBar';
import { MobileNav } from './MobileNav';
import { cn } from '@/lib/cn';
import { ThemeApplier } from '@/lib/prefs/usePrefsStore';
import { FavoritableProvider } from '@/features/favorites/FavoritableContext';
import { NotificationBanner } from '@/features/notifications/NotificationBanner';
import { NotificationsInitializer } from '@/features/notifications/NotificationsInitializer';
import { useAboveBreakpoint } from '@/lib/hooks/useBreakpoint';
import { PlanAlertBanner } from '@onxite/plan-sdk';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/lib/i18n/useT';
import { useAuthStore } from '@/lib/auth/authStore';
// Caso 07 — AI assistant launcher + drawer.
import { AssistantLauncher } from '@/features/ai-assistant/AssistantLauncher';
import { AssistantDrawer } from '@/features/ai-assistant/AssistantDrawer';
// PRD §13.3 — first-login Data Controller Ack gate.
import { LegalGate } from '@/features/legal/LegalGate';
// Product tours — auto-runs the current page's walkthrough on first visit.
import { TourController } from '@/features/tour/TourController';

export function AdminShell() {
  const leftOpen = useSidebar((s) => s.open);
  const mobileOpen = useSidebar((s) => s.mobileOpen);
  const closeMobile = useSidebar((s) => s.closeMobile);
  // El hueco de la derecha: lo calcula el propio carril, que es quien sabe si
  // tiene algo que enseñar. Cero cuando no lo tiene.
  const railOffset = useRightRailOffset();
  // Right sidebar is hidden below lg — only reserve space when the viewport
  // actually shows it.
  const isLgUp = useAboveBreakpoint('lg');
  const rightOffset = isLgUp ? railOffset : 0;
  const app = useAuthStore((s) => s.app);

  // Caso 11 — operator sessions get minimal chrome: no sidebar, no top bar,
  // no AI launcher, no plan banners. A booth operator only ever sees their
  // single station view; the full admin frame would just be noise (and the
  // sidebar links would 403 against the restricted operator JWT).
  //
  // BUG-fix: scope the shell-less mode to OPERATOR-SPECIFIC paths. Without
  // this guard, any time an operator JWT pollutes localStorage (because a
  // brand manager opened a station QR / operator-login link in the same
  // browser tab) the entire admin UI loses its sidebar + top bar on the
  // next navigation — exactly what gets reported as "todo se ha perdido"
  // after clicking back from the event report.
  const location = useLocation();
  const isOperatorRoute =
    location.pathname.startsWith('/operator/') ||
    location.pathname === '/operator-login' ||
    location.pathname === '/redeem';
  if (app === 'operator' && isOperatorRoute) {
    return (
      <FavoritableProvider>
        <div className="min-h-screen bg-[var(--color-xk-bg)]">
          <ThemeApplier />
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </FavoritableProvider>
    );
  }

  return (
    <FavoritableProvider>
      <div className="min-h-screen bg-[var(--color-xk-bg)]">
        <ThemeApplier />
        <TourController />
        {/* El cromo no se imprime: el PDF de un informe es sólo el informe. */}
        <div data-print="hide" className="contents">
          <Sidebar />
          <RightSidebar />
        </div>
        {/* Mobile backdrop — appears under the drawer, dismisses on tap. */}
        {mobileOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMobile}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          />
        )}
        <div
          className={cn(
            'flex min-h-screen flex-col transition-[padding] duration-150',
            // Mobile: no left padding — sidebar is an overlay drawer.
            // Desktop (md+): reserve room for the inline sidebar.
            leftOpen ? 'md:pl-60' : 'md:pl-16',
          )}
          style={{ paddingRight: rightOffset }}
          data-print="content"
        >
          <div data-print="hide" className="contents">
            <TopBar />
            <NotificationBanner />
            <PlanAlertBannerHost />
          </div>
          <NotificationsInitializer />
          {/* pb-16 on mobile reserves room for the fixed bottom nav. */}
          <main className="flex-1 pb-16 md:pb-0">
            <Outlet />
          </main>
        </div>
        {/* Caso 07 — AI assistant: launcher + drawer mounted globally so
            any route can open the chat without re-instantiating it. */}
        <div data-print="hide" className="contents">
          <AssistantLauncher />
          <AssistantDrawer />
        </div>
        {/* PRD §13.3 — Data Controller Ack first-login gate. Renders a full-
            screen modal if the user has not accepted the current DCA hash. */}
        <LegalGate />
        {/* Mobile primary nav — fixed bottom bar + "More" sheet (md:hidden). */}
        <div data-print="hide" className="contents">
          <MobileNav />
        </div>
      </div>
    </FavoritableProvider>
  );
}

/**
 * Renders the plan-SDK banner with app-aware navigation. Kept as a tiny child
 * so AdminShell itself doesn't need a useNavigate hook hanging around.
 * The banner self-hides when there's no active planAlert in context.
 */
function PlanAlertBannerHost() {
  const navigate = useNavigate();
  const t = useT();
  // One dismissal per alert kind, persisted, so clearing the bar on one page
  // does not bring it straight back on the next navigation. A worse alert
  // (e.g. LIMIT_NEAR → LIMIT_CRITICAL) is a different key and speaks up again.
  const dismissed = useDismissedPlanAlerts((s) => s.dismissed);
  const dismiss = useDismissedPlanAlerts((s) => s.dismiss);
  // Caso 15 v2 — Render with kind-aware styling and an explicit "Upgrade
  // plan" CTA when the alert is LIMIT_CRITICAL (one-shot ≥95%). The
  // package's default render covers EXPIRING_SOON / LIMIT_NEAR / EXPIRED;
  // here we override `render` so the critical state pops.
  return (
    <PlanAlertBanner
      ctaLabel={t('plan.banner.viewPlans')}
      onNavigate={(url) => navigate(url)}
      messages={{
        EXPIRING_SOON: t('plan.banner.expiringSoon'),
        LIMIT_NEAR: t('plan.banner.limitNear'),
        LIMIT_CRITICAL: t('plan.banner.limitCritical'),
        EXPIRED: t('plan.banner.expired'),
      }}
      render={(alert) => {
        if (dismissed[alert.kind]) return <></>;
        // The CTA target is owned by the app, not the SDK/BE — the
        // backend's `alert.ctaUrl` and the SDK default both point at a
        // route this app doesn't have (`/billing/plans`), which the
        // catch-all bounces to `/`. Always use the real billing route.
        const ctaUrl = '/settings/billing';
        const critical = alert.kind === 'LIMIT_CRITICAL' || alert.kind === 'EXPIRED';
        const bg = critical
          ? 'bg-[color-mix(in_srgb,var(--color-error)_18%,var(--color-bg-surface))]'
          : 'bg-[var(--color-bg-surface)]';
        const text = critical
          ? t('plan.banner.upgradeNow')
          : t('plan.banner.viewPlans');
        return (
          <div
            role="alert"
            className={`flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border-default)] px-4 py-2 text-xs text-[var(--color-fg-primary)] ${bg}`}
            data-plan-alert-kind={alert.kind}
          >
            <span>
              {alert.kind === 'EXPIRING_SOON'
                ? t('plan.banner.expiringSoon')
                : alert.kind === 'LIMIT_NEAR'
                ? t('plan.banner.limitNear')
                : alert.kind === 'LIMIT_CRITICAL'
                ? t('plan.banner.limitCritical')
                : t('plan.banner.expired')}
              {alert.daysRemaining != null && alert.kind === 'EXPIRING_SOON' && (
                <span className="ml-1 opacity-70">
                  ({alert.daysRemaining}{' '}
                  {alert.daysRemaining === 1
                    ? t('plan.banner.day')
                    : t('plan.banner.days')})
                </span>
              )}
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(ctaUrl)}
                className={
                  critical
                    ? 'rounded-md bg-[var(--color-error)] px-3 py-1 font-bold text-white hover:opacity-90'
                    : 'font-semibold text-[var(--color-accent-ink)] underline'
                }
              >
                {text}
              </button>
              <button
                type="button"
                onClick={() => dismiss(alert.kind)}
                aria-label={t('plan.banner.dismiss')}
                title={t('plan.banner.dismiss')}
                className="grid h-6 w-6 flex-none place-items-center rounded transition-colors hover:bg-[var(--color-bg-elevated)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        );
      }}
    />
  );
}

/**
 * Remembers which plan-alert kinds the user has dismissed. Persisted so the
 * single upgrade bar stays gone while navigating, instead of re-appearing on
 * every page the way the old per-page quota banners did.
 */
interface DismissedPlanAlerts {
  dismissed: Record<string, true>;
  dismiss: (kind: string) => void;
}

const useDismissedPlanAlerts = create<DismissedPlanAlerts>()(
  persist(
    (set) => ({
      dismissed: {},
      dismiss: (kind) => set((s) => ({ dismissed: { ...s.dismissed, [kind]: true } })),
    }),
    { name: 'xotski.planAlertDismissed', version: 1 },
  ),
);
